import { Children, type ReactNode, useEffect, useState } from "react";

import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

/** Switch from dots to numbered pages when there are this many snaps or more. */
const NUMBERED_PAGINATION_THRESHOLD = 8;
/** How strongly scale falls off away from the centered snap. */
const TWEEN_FACTOR_BASE = 0.35;
/** Minimum scale for slides away from center. */
const MIN_SCALE = 0.88;

type EmblaApi = NonNullable<ReturnType<typeof useEmblaCarousel>[1]>;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const CarouselRoot = styled.div`
  width: 0;
  min-width: 100%;
  padding: 0;
`;

const Stage = styled.div`
  position: relative;
  padding: 14px;
`;

const Viewport = styled.div`
  overflow: hidden;
  /* Extra room inside the clip so card shadows aren't cut off. */
  padding: 6px;
`;

const Container = styled.div`
  display: flex;
  gap: 12px;
  touch-action: pan-y pinch-zoom;
`;

const Slide = styled.div`
  flex: 0 0 200px;
  min-width: 0;
  display: flex;
`;

const SlideInner = styled.div`
  display: flex;
  width: 100%;
  transform-origin: center center;
  will-change: transform;
`;

const NavButton = styled(Button)`
  && {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    margin: 0;
    border-radius: 50%;
  }
`;

const PrevButton = styled(NavButton)`
  && {
    left: -6px;
  }
`;

const NextButton = styled(NavButton)`
  && {
    right: -6px;
  }
`;

const Pagination = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.25rem;
  padding: 0 8px;
`;

const Dot = styled.button<{ $selected: boolean }>`
  appearance: none;
  touch-action: manipulation;
  cursor: pointer;
  border: 0;
  padding: 0;
  margin: 0;
  width: ${({ $selected }) => ($selected ? "16px" : "8px")};
  height: 8px;
  border-radius: 4px;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary.solid : theme.colors.primary.softer};
  transition:
    width 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.soft};
  }
`;

const PageNumber = styled.button<{ $selected: boolean }>`
  appearance: none;
  touch-action: manipulation;
  cursor: pointer;
  border: 0;
  padding: 0.15em 0.45em;
  margin: 0;
  min-width: 1.5em;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary.solid : "rgba(0, 0, 0, 0.45)"};
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary.softer : "transparent"};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.solid};
    background-color: ${({ theme }) => theme.colors.primary.softer};
  }
`;

type CarouselState = {
  selectedIndex: number;
  scrollSnaps: number[];
  canScrollPrev: boolean;
  canScrollNext: boolean;
};

const INITIAL_STATE: CarouselState = {
  selectedIndex: 0,
  scrollSnaps: [],
  canScrollPrev: false,
  canScrollNext: false,
};

const syncCarouselState = (api: EmblaApi): CarouselState => ({
  selectedIndex: api.selectedScrollSnap(),
  scrollSnaps: api.scrollSnapList(),
  canScrollPrev: api.canScrollPrev(),
  canScrollNext: api.canScrollNext(),
});

/** Apply center-weighted scale to slide inners; runs on scroll / reInit. */
const applyScaleTween = (
  api: EmblaApi,
  tweenNodes: (HTMLElement | null)[],
  tweenFactor: number,
  eventName?: string,
) => {
  const engine = api.internalEngine();
  const scrollProgress = api.scrollProgress();
  const slidesInView = api.slidesInView();
  const isScrollEvent = eventName === "scroll";

  api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
    const diffToTarget = scrollSnap - scrollProgress;
    const slidesInSnap = engine.slideRegistry[snapIndex] ?? [];

    slidesInSnap.forEach((slideIndex) => {
      if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

      const scale = clamp(
        1 - Math.abs(diffToTarget * tweenFactor),
        MIN_SCALE,
        1,
      );
      const node = tweenNodes[slideIndex];
      if (node) node.style.transform = `scale(${scale})`;
    });
  });
};

const getTweenNodes = (api: EmblaApi) =>
  api
    .slideNodes()
    .map(
      (slideNode) =>
        slideNode.querySelector("[data-carousel-scale]") as HTMLElement | null,
    );

type HomeCarouselProps = { children: ReactNode };

const HomeCarousel = ({ children }: HomeCarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    // Contain edge empty space (flush-left start) but keep one snap per slide.
    // trimSnaps collapses short carousels (e.g. 2 cards) into a single snap,
    // which hides arrows and leaves neighbouring cards at full scale with no gap.
    containScroll: "keepSnaps",
  });
  const [
    { selectedIndex, scrollSnaps, canScrollPrev, canScrollNext },
    setState,
  ] = useState(INITIAL_STATE);
  const slides = Children.toArray(children);

  useEffect(() => {
    if (!emblaApi) return;

    let tweenNodes = getTweenNodes(emblaApi);
    let tweenFactor = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;

    const refreshTween = (api: EmblaApi, eventName?: string) => {
      applyScaleTween(api, tweenNodes, tweenFactor, eventName);
    };

    const onReInit = (api: EmblaApi) => {
      tweenNodes = getTweenNodes(api);
      tweenFactor = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
      setState(syncCarouselState(api));
      refreshTween(api);
    };

    const onSelect = (api: EmblaApi) => {
      setState((prev) => ({
        ...prev,
        selectedIndex: api.selectedScrollSnap(),
        canScrollPrev: api.canScrollPrev(),
        canScrollNext: api.canScrollNext(),
      }));
    };

    onReInit(emblaApi);
    emblaApi
      .on("reInit", onReInit)
      .on("select", onSelect)
      .on("scroll", refreshTween)
      .on("slideFocus", refreshTween);

    return () => {
      emblaApi
        .off("reInit", onReInit)
        .off("select", onSelect)
        .off("scroll", refreshTween)
        .off("slideFocus", refreshTween);
    };
  }, [emblaApi]);

  const showControls = scrollSnaps.length > 1;
  const useNumbers = scrollSnaps.length >= NUMBERED_PAGINATION_THRESHOLD;

  return (
    <CarouselRoot>
      <Stage>
        <Viewport ref={emblaRef}>
          <Container>
            {slides.map((child, index) => (
              <Slide key={index}>
                <SlideInner data-carousel-scale>{child}</SlideInner>
              </Slide>
            ))}
          </Container>
        </Viewport>
        {showControls ? (
          <>
            <PrevButton
              color="primary"
              disabled={!canScrollPrev}
              variant="soft"
              size="small"
              onClick={() => emblaApi?.scrollPrev()}
              aria-label="Previous"
            >
              <Icon icon={ChevronLeftIcon} />
            </PrevButton>
            <NextButton
              color="primary"
              disabled={!canScrollNext}
              variant="soft"
              size="small"
              onClick={() => emblaApi?.scrollNext()}
              aria-label="Next"
            >
              <Icon icon={ChevronRightIcon} />
            </NextButton>
          </>
        ) : null}
      </Stage>
      {showControls ? (
        <Pagination>
          {scrollSnaps.map((_, index) =>
            useNumbers ? (
              <PageNumber
                key={index}
                type="button"
                $selected={index === selectedIndex}
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === selectedIndex ? "true" : undefined}
              >
                {index + 1}
              </PageNumber>
            ) : (
              <Dot
                key={index}
                type="button"
                $selected={index === selectedIndex}
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === selectedIndex ? "true" : undefined}
              />
            ),
          )}
        </Pagination>
      ) : null}
    </CarouselRoot>
  );
};

export default HomeCarousel;
